import { useEffect, useState } from 'react';
import '../../styles/csrEmailTable.css';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
// eslint-disable-next-line import/extensions
import { FaEye } from 'react-icons/fa';
import useSystemRequirements from '../../hooks/useSystemRequirement.js';

const EmailLogTable = ({ onBack }) => {
    const { fetchCsrEmailLogs, csrLogs, loading, error } =
        useSystemRequirements();

    const [page, setPage] = useState(1);
    const [officeSearch, setOfficeSearch] = useState('');
    const [debouncedOfficeSearch, setDebouncedOfficeSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortBy, setSortBy] = useState('sentAt:desc');
    const [showModal, setShowModal] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    const limit = 20;

    // Debounce office name search
    useEffect(() => {
        const handler = debounce(() => {
            setDebouncedOfficeSearch(officeSearch.trim());
            setPage(1);
        }, 400);
        handler();
        return () => handler.cancel();
    }, [officeSearch]);

    // Fetch logs when filters change
    useEffect(() => {
        fetchCsrEmailLogs({
            officeName: debouncedOfficeSearch,
            startDate,
            endDate,
            sortBy,
            page,
            limit
        });
    }, [debouncedOfficeSearch, startDate, endDate, sortBy, page]);

    const handlePreview = (computer, log) => {
        setPreviewData({ computer, log });
        setShowModal(true);
    };

    const handlePrev = () => {
        if (page > 1) setPage((prev) => prev - 1);
    };

    const handleNext = () => {
        if (csrLogs.totalPages && page < csrLogs.totalPages) {
            setPage((prev) => prev + 1);
        }
    };

    // Sorting logic
    const handleSort = (field) => {
        const [currentField, currentOrder] = sortBy.split(':');
        const newOrder =
            currentField === field && currentOrder === 'asc' ? 'desc' : 'asc';
        setSortBy(`${field}:${newOrder}`);
        setPage(1);
    };

    const getSortIcon = (field) => {
        const [currentField, currentOrder] = sortBy.split(':');
        if (field !== currentField) return '';
        return currentOrder === 'asc' ? '↑' : '↓';
    };

    // Rendering Logic
    let content = null;
    const results = Array.isArray(csrLogs?.docs) ? csrLogs.docs : [];

    if (loading.csrLogs) {
        content = <p>Loading logs...</p>;
    } else if (error) {
        content = <p className="error-text">{error.message}</p>;
    } else if (results.length === 0) {
        content = <p>No logs found</p>;
    } else {
        content = (
            <>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: '1rem'
                    }}>
                    <p style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        Total Email Logs:{' '}
                        <span style={{ color: '#007bff' }}>
                            {csrLogs.totalDocs || 0}
                        </span>
                    </p>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Serial No</th>
                            <th
                                onClick={() => handleSort('officeName')}
                                style={{ cursor: 'pointer' }}>
                                Office Name {getSortIcon('officeName')}
                            </th>
                            <th
                                onClick={() => handleSort('sentAt')}
                                style={{ cursor: 'pointer' }}>
                                Date Sent {getSortIcon('sentAt')}
                            </th>
                            <th>Computer Systematic Name</th>
                            <th
                                onClick={() =>
                                    handleSort('flaggedComputers.score')
                                }
                                style={{ cursor: 'pointer' }}>
                                Scores {getSortIcon('flaggedComputers.score')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((log, index) => (
                            <tr key={log._id}>
                                <td>{(page - 1) * limit + index + 1}</td>{' '}
                                {/* ← Serial No logic */}
                                <td>
                                    <a
                                        href={`/office/${log.officeId}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontWeight: 'bold' }}>
                                        {log.officeName}
                                    </a>
                                </td>
                                <td>
                                    {log.sentAt
                                        ? new Date(log.sentAt).toLocaleString()
                                        : '—'}
                                </td>
                                <td>
                                    <ul
                                        style={{
                                            paddingLeft: '0px',
                                            margin: 0
                                        }}>
                                        {log.flaggedComputers.map((pc, idx) => (
                                            <li
                                                key={idx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    padding: '5px 0'
                                                }}>
                                                {pc.computerSysName} -{' '}
                                                {pc.computerName}
                                                <button
                                                    onClick={() =>
                                                        handlePreview(pc, log)
                                                    }
                                                    title="Preview Email"
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: '#007bff'
                                                    }}>
                                                    <FaEye size={16} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </td>
                                <td>
                                    <ul
                                        style={{
                                            paddingLeft: '16px',
                                            margin: 0
                                        }}>
                                        {log.flaggedComputers.map((pc, idx) => (
                                            <li
                                                key={idx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    padding: '5px 0'
                                                }}>
                                                {pc.score}
                                            </li>
                                        ))}
                                    </ul>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="pagination" style={{ marginTop: '2rem' }}>
                    <button
                        disabled={page === 1 || loading.csrLogs}
                        onClick={handlePrev}>
                        Prev
                    </button>
                    <span>
                        Page {page} of {csrLogs.totalPages || 1}
                    </span>
                    <button
                        disabled={page === csrLogs.totalPages}
                        onClick={handleNext}>
                        Next
                    </button>
                </div>
                {/* Email Preview Modal */}
                {showModal && previewData && (
                    <div
                        className="modal-overlay"
                        style={{
                            position: 'fixed',
                            // top: 0,
                            // left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999
                        }}>
                        <div
                            className="modal-content"
                            style={{
                                backgroundColor: '#fff',
                                padding: '2rem',
                                borderRadius: '8px',
                                width: '45%',
                                maxHeight: '80vh',
                                overflowY: 'auto',
                                position: 'relative'
                            }}>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '18px',
                                    cursor: 'pointer'
                                }}>
                                ×
                            </button>
                            <h3>Email Preview</h3>
                            <p>
                                <strong>Office Name:</strong>{' '}
                                {previewData.log.officeName}
                            </p>
                            <p>
                                <strong>Computer Systematic Name:</strong>{' '}
                                {previewData.computer.computerSysName}
                            </p>
                            <p>
                                <strong>Score:</strong>{' '}
                                {previewData.computer.score}
                            </p>
                            <p>
                                <strong>Sent At:</strong>{' '}
                                {new Date(
                                    previewData.log.sentAt
                                ).toLocaleString()}
                            </p>
                            <hr />
                            <div
                                style={{
                                    border: '1px solid #ddd',
                                    marginTop: '1rem',
                                    padding: '1rem',
                                    backgroundColor: '#fafafa',
                                    maxHeight: '60vh',
                                    overflowY: 'auto'
                                }}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        previewData.computer.emailBody ||
                                        '<p>No preview available</p>'
                                }}></div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="email-log-container">
            <div style={{ position: 'relative' }}>
                <button className="back-button" onClick={onBack}>
                    ← Back
                </button>
            </div>
            <h2>Email Log</h2>

            {/* Filters */}
            <div className="filters">
                <div className="filter-group">
                    <label htmlFor="officeSearch">Office Name</label>
                    <input
                        id="officeSearch"
                        type="text"
                        value={officeSearch}
                        onChange={(e) => setOfficeSearch(e.target.value)}
                        placeholder="Search by office name..."
                        className="input"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="startDate">Start Date</label>
                    <input
                        id="startDate"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="endDate">End Date</label>
                    <input
                        id="endDate"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="sortBy">Sort By</label>
                    <select
                        id="sortBy"
                        value={sortBy}
                        onChange={(e) => {
                            setSortBy(e.target.value);
                            setPage(1);
                        }}>
                        <option value="sentAt:desc">Newest First</option>
                        <option value="sentAt:asc">Oldest First</option>
                    </select>
                </div>
            </div>

            {content}
        </div>
    );
};

EmailLogTable.propTypes = {
    onBack: PropTypes.func.isRequired
};

export default EmailLogTable;
